'use client';
import React, { useEffect } from 'react';
import { QuantitySelector as SharedQuantitySelector } from '@launchstore/shared-puck';
import type { QuantitySelectorProps, SetQuantity } from '@launchstore/shared-puck';
import { useProduct, useVariantSelection } from '@/lib/hooks/useProduct';

const SharedQuantitySelectorRender = (SharedQuantitySelector as any).render;

const QuantitySelectorImpl: React.FC<QuantitySelectorProps> = (props: any) => {
  const { product, quantity, setQuantity } = useProduct() as any;
  const { selectedVariant } = useVariantSelection() as any;
  // Only pass controlled props if the cart context is present.
  const controlled = quantity !== undefined && typeof setQuantity === 'function';
  const setQ: SetQuantity = (q: number) => (typeof setQuantity === 'function' ? setQuantity(q) : undefined);

  // Cap the stepper at the tracked inventory of the effective variant
  // (shopper's selection, else the first/default variant). Uncapped when
  // inventory is untracked or backorders are allowed — same canonical
  // rules as the stock indicator and the AddToCart button.
  const effectiveVariant = selectedVariant ?? product?.variants?.[0] ?? null;
  const trackedStock =
    effectiveVariant &&
    effectiveVariant.manage_inventory !== false &&
    !effectiveVariant.allow_backorder &&
    (effectiveVariant.inventory_quantity ?? 0) > 0
      ? effectiveVariant.inventory_quantity
      : null;
  const configuredMax = typeof props.maxQuantity === 'number' ? props.maxQuantity : 99;
  const maxQuantity = trackedStock ? Math.min(trackedStock, configuredMax) : configuredMax;

  // Re-clamp the cart quantity when the cap tightens (e.g. shopper picked
  // qty 5, then switched to a variant with stock 2).
  useEffect(() => {
    if (controlled && trackedStock && quantity > trackedStock) {
      setQuantity(Math.max(1, trackedStock));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedStock]);

  return (
    <SharedQuantitySelectorRender
      {...(props as any)}
      maxQuantity={maxQuantity}
      quantity={controlled ? quantity : undefined}
      setQuantity={controlled ? setQ : undefined}
    />
  );
};

export const QuantitySelector: any = { ...SharedQuantitySelector, render: (props: any) => <QuantitySelectorImpl {...props} /> };
export default QuantitySelector;
